import { Progress } from 'modules/types'
import { Dict } from 'types/generics'
import { AddError, EntityMetadata, FetchError, RemoveError, UpdateError } from 'modules/domain/types'

export enum SeedPedigree {
  Certified = 'Certified',
  Registered = 'Registered',
  Foundation = 'Foundation',
  Select = 'Select',
  Breeder = 'Breeder',
}

export enum SeedBrand {
  CSL = 'CSL',
  ASC = 'ASC',
}

export type SeedTrackerListRequestFilter = {
  search?: string
  product_id?: string
  seed_crop_id?: string
  seed_class_id?: string
  seed_brand?: SeedBrand
  company_id?: string
}

export type SeedTracker = {
  id: string
  brand: string | null
  variety: string
  seed_crop_name: string
  seed_class_name: string
  total_available_mt?: string | null
  total_pending_sale_mt?: string | null
  total_on_hand_mt?: string | null
  pedigree_breakdown?: SeedTrackerPedigreeBreakdown[]
}

export type SeedTrackerPedigreeBreakdown = {
  pedigree: SeedPedigree
  available_mt?: string | null
  pending_sale_mt?: string | null
  on_hand_mt?: string | null
}

export type SeedTrackerDTO = Omit<SeedTracker, 'id'>

export type SeedTrackerListRequestSorting = {
  sort_field?: keyof SeedTracker
  sort_reversed?: boolean
}

export type SeedTrackerState = {
  items: Dict<SeedTracker>
  meta: Dict<EntityMetadata<SeedTracker>>
  ids: string[]
  listFetchProgress: Progress
  listFetchError: FetchError | null
  listFetchNextProgress: Progress
  listFetchNextError: FetchError | null
  itemFetchProgress: Progress
  itemFetchError: FetchError | null
  addProgress: Progress
  addError: AddError | null
  updateProgress: Progress
  updateError: UpdateError | null
  removeProgress: Progress
  removeError: RemoveError | null

  filter: SeedTrackerListRequestFilter
  sorting: SeedTrackerListRequestSorting
  page: number
  total: number
  pageSize: number
  listMeta?: SeedTrackerListMeta
}

export type SeedTrackerListMeta = {
  totals?: {
    total_available_mt?: string | null
    total_pending_sale_mt?: string | null
    total_on_hand_mt?: string | null
  }
}

export type SeedTrackerListResponse = {
  total_count: number
  page: number
  page_size: number
  data: SeedTracker[]
  meta?: SeedTrackerListMeta
}

export type FormData = Partial<SeedTracker>

export type SeedInventoryCompanySnapshot = {
  id: string
  official_name: string
}

export type SeedInventoryApiRecord = {
  id: string
  product_card_id: string
  company_id: string
  company?: SeedInventoryCompanySnapshot | null
  region?: string | null
  contact_name?: string | null
  contact_number?: string | null
  contact_email?: string | null
  year: string
  pending_sale_mt?: string | null
  available_mt?: string | null
  disposed_mt?: string | null
  on_hand_mt?: string | null
  clean_yield_actual_mt?: string | null
  clean_yield_estimate_mt?: string | null
  gross_yield_actual_bu?: string | null
  gross_yield_estimate_bu?: string | null
  initial_germ_actual?: string | null
  initial_germ_estimate?: string | null
  final_germ_actual?: string | null
  final_germ_estimate?: string | null
  diseased_actual?: string | null
  diseased_estimate?: string | null
  planned_seeded_area?: string | null
  current_seeded_area_actual?: string | null
  current_seeded_area_estimate?: string | null
  seeded_pedigree: SeedPedigree
  projected_pedigree: SeedPedigree
  projected_yield_actual_bu?: string | null
  projected_yield_estimate_bu?: string | null
  created_at?: string | null
  updated_at?: string | null
  last_updated_date?: string | null
  crop_certificate_id?: string | null
}

export type SeedInventoryTotalsApi = {
  current_seeded_area_actual?: string | null
  on_hand_mt?: string | null
  pending_sale_mt?: string | null
  available_mt?: string | null
  disposed_mt?: string | null
  clean_yield_estimate_mt?: string | null
  clean_yield_actual_mt?: string | null
  gross_yield_actual_bu?: string | null
  gross_yield_estimate_bu?: string | null
  initial_germ_actual?: string | null
  final_germ_actual?: string | null
  diseased_actual?: string | null
}

export type SeedInventoryListMeta = {
  totals?: SeedInventoryTotalsApi
}

export type SeedInventoryCurrentMtApi = {
  available_mt?: string | null
}
